import { z } from 'zod';

/**
 * Факт оплат из money-портала Infratech (ФН-04, ИН-07). Сторона money-портала описана в плане запуска
 * («Money-портал: API для EPC»): GET /sync/epc/payments/ с курсором и заголовком X-EPC-Key, GET /sync/epc/health/.
 * Платежи физлицам и зарплата не выдаются — только их число в `meta.excluded`.
 */

/** Одна строка ответа money-портала. Сумма — строкой с двумя знаками */
export const MoneyPortalItem = z.object({
  id: z.string().min(1).max(200),
  source: z.enum(['bank', '1c']),
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .refine(
      (d) =>
        Number.isFinite(Date.parse(`${d}T00:00:00Z`)) &&
        new Date(`${d}T00:00:00Z`).toISOString().startsWith(d),
      'Неверная дата',
    ),
  direction: z.enum(['in', 'out']),
  amount: z.string().regex(/^\d+(\.\d{1,2})?$/),
  currency: z.string().max(8).default('KZT'),
  counterparty: z
    .object({ name: z.string().max(500).nullish(), bin: z.string().max(20).nullish() })
    .default({}),
  purpose: z.string().max(2000).nullish(),
  doc_number: z.string().max(200).nullish(),
  knp: z.string().max(10).nullish(),
  account_last4: z.string().max(8).nullish(),
  registry: z.object({ number: z.string().max(100), request: z.string().max(100).nullish() }).nullish(),
  updated_at: z.string().max(40).nullish(),
});
export type MoneyPortalItem = z.infer<typeof MoneyPortalItem>;

export const MoneyPortalPage = z.object({
  items: z.array(z.unknown()).max(500),
  next: z
    .string()
    .max(500)
    .nullish()
    .transform((v) => v ?? null),
  meta: z.object({ excluded: z.number().int().nonnegative().default(0) }).default({ excluded: 0 }),
});

export const MONEY_PAYMENT_STATUSES = {
  matched: 'Разнесена',
  unmatched: 'Не разнесена',
  ignored: 'Снята с учёта',
} as const;
export type MoneyPaymentStatus = keyof typeof MONEY_PAYMENT_STATUSES;

export const MoneyPaymentView = z.object({
  id: z.uuid(),
  externalId: z.string(),
  source: z.enum(['bank', '1c']),
  paidOn: z.string(),
  direction: z.enum(['in', 'out']),
  /** null — у человека нет уровня денег «Акты и счета» */
  amount: z.number().nullable(),
  currency: z.string(),
  counterpartyName: z.string().nullable(),
  counterpartyBin: z.string().nullable(),
  purpose: z.string().nullable(),
  docNumber: z.string().nullable(),
  accountLast4: z.string().nullable(),
  registryNumber: z.string().nullable(),
  status: z.enum(['matched', 'unmatched', 'ignored']),
  statusTitle: z.string(),
  paymentRequestId: z.uuid().nullable(),
  paymentRequestNumber: z.string().nullable(),
  projectId: z.uuid().nullable(),
  matchedBy: z.enum(['auto', 'manual']).nullable(),
  reason: z.string().nullable(),
  /** Подходящие заявки на оплату: подсказки для ручного разнесения */
  suggestions: z.array(z.object({ id: z.uuid(), number: z.string(), score: z.number() })),
});
export type MoneyPaymentView = z.infer<typeof MoneyPaymentView>;

export const MoneyPaymentsQuery = z.object({
  status: z.enum(['matched', 'unmatched', 'ignored']).optional(),
  direction: z.enum(['in', 'out']).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

/** Ручное разнесение: на заявку на оплату, на проект или снять с учёта */
export const MoneyPaymentResolve = z.discriminatedUnion('action', [
  z.strictObject({ action: z.literal('request'), paymentRequestId: z.uuid() }),
  z.strictObject({
    action: z.literal('project'),
    projectId: z.uuid(),
    contractId: z.uuid().nullable().default(null),
    costArticle: z.string().trim().max(120).nullable().default(null),
  }),
  z.strictObject({ action: z.literal('ignore'), reason: z.string().trim().min(3).max(500) }),
]);
export type MoneyPaymentResolve = z.infer<typeof MoneyPaymentResolve>;

/** Настройки сопоставления в настройках компании (окно даты, допуск суммы) */
export const MONEY_MATCH_DEFAULTS = { dateWindowDays: 14, amountTolerance: 0.5 } as const;
export const MoneyMatchSettings = z.object({
  dateWindowDays: z.number().int().min(0).max(120),
  amountTolerance: z.number().min(0).max(1000),
});
export type MoneyMatchSettings = z.infer<typeof MoneyMatchSettings>;

export const MoneySyncResult = z.object({
  loaded: z.number(),
  matched: z.number(),
  unmatched: z.number(),
  excluded: z.number(),
  pages: z.number(),
  /** Строки, которые портал не принял: неверный формат или не удалось сохранить */
  rejected: z.number().default(0),
});
export type MoneySyncResult = z.infer<typeof MoneySyncResult>;
